// src/pages/admin/TaskCalendarView.jsx
import React, { useState } from "react";
import { useTasks } from "../../context/TaskContext";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useEmployees } from "../../context/EmployeeContext";
import { useProjects } from "../../context/ProjectContext";
import { formatDate } from "../../utils/dateUtils";

// ─── HELPERS ────────────────────────────────────────────────────────────────

const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
const getFirstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

/**
 * Normalise any date value coming from MySQL (ISO string, Date object, or
 * plain "YYYY-MM-DD") to a plain "YYYY-MM-DD" string. This is the root fix
 * for why tasks never appeared on the calendar.
 */
const toDateStr = (val) => {
  if (!val) return null;
  if (typeof val === "string") return val.slice(0, 10); // handles ISO & plain
  if (val instanceof Date) return val.toISOString().slice(0, 10);
  return null;
};

const PRIORITY_STYLES = {
  High:   { dot: "bg-red-500",    pill: "bg-red-100 text-red-700 border-red-200"   },
  Medium: { dot: "bg-yellow-500", pill: "bg-yellow-100 text-yellow-700 border-yellow-200" },
  Low:    { dot: "bg-green-500",  pill: "bg-green-100 text-green-700 border-green-200"  },
};

const STATUS_STYLES = {
  "Completed":  "bg-green-100 text-green-700",
  "In Progress":"bg-blue-100 text-blue-700",
  "Pending":    "bg-orange-100 text-orange-700",
  "On Hold":    "bg-gray-100 text-gray-600",
};

const MONTH_NAMES = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December"
];

// ─── COMPONENT ───────────────────────────────────────────────────────────────

const TaskCalendarView = ({ tasks: propTasks }) => {
  const { tasks: contextTasks } = useTasks();
  const rawTasks  = propTasks || contextTasks;
  const navigate  = useNavigate();
  const { checkAccess } = useAuth();
  const { employees } = useEmployees();
  const { projects }  = useProjects();

  const [currentDate, setCurrentDate]   = useState(new Date());
  const [hoveredTask, setHoveredTask]   = useState(null);   // { task, x, y }
  const [selectedDay, setSelectedDay]   = useState(null);   // day object for modal

  const year  = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const changeMonth = (offset) => {
    setSelectedDay(null);
    setCurrentDate(new Date(year, month + offset, 1));
  };

  // ── Normalise all task dates once ─────────────────────────────────────────
  const tasks = rawTasks.map(t => ({
    ...t,
    _dueDate:   toDateStr(t.dueDate),
    _startDate: toDateStr(t.startDate),
  }));

  // ── Build calendar grid ───────────────────────────────────────────────────
  const numDays  = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);

  const calendarDays = [];
  for (let i = 0; i < firstDay; i++) calendarDays.push(null);

  for (let d = 1; d <= numDays; d++) {
    const dayStr = `${year}-${String(month + 1).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
    const dayTasks = tasks.filter(t => t._dueDate === dayStr || t._startDate === dayStr);
    calendarDays.push({ day: d, dateStr: dayStr, tasks: dayTasks });
  }

  // ── Lookup helpers ────────────────────────────────────────────────────────
  const getEmployee = (t) => {
    if (t.assignedEmployee) return t.assignedEmployee;
    if (t.assignedName && !t.assignedName.startsWith("Employee #")) {
      return { name: t.assignedName, employeeName: t.assignedName, role: t.assignedRole || "" };
    }
    if (t.assignedTo && !t.assignedTo.startsWith("Employee #")) {
      return { name: t.assignedTo, employeeName: t.assignedTo, role: t.assignedRole || "" };
    }
    return employees.find(e =>
      (t.assignedEmployeeId && String(e.id) === String(t.assignedEmployeeId)) ||
      (t.employeeId         && String(e.id) === String(t.employeeId)) ||
      (t.assigned_to        && String(e.id) === String(t.assigned_to))
    );
  };

  const getProject = (t) =>
    projects.find(p => String(p.id) === String(t.projectId));

  const handleTaskClick = (taskId) => {
    if (checkAccess("task.view")) navigate(`/admin/task/view/${taskId}`);
  };

  // ── Today string for highlight ────────────────────────────────────────────
  const todayStr = new Date().toISOString().slice(0, 10);
  const isToday  = (dateStr) => dateStr === todayStr;

  // ── Summary counts for the header strip ──────────────────────────────────
  const monthStr = `${year}-${String(month + 1).padStart(2, "0")}`;
  const monthTasks = tasks.filter(
    t => (t._dueDate || "").startsWith(monthStr) || (t._startDate || "").startsWith(monthStr)
  );
  const counts = {
    total:      monthTasks.length,
    high:       monthTasks.filter(t => t.priority === "High").length,
    inProgress: monthTasks.filter(t => t.status === "In Progress").length,
    completed:  monthTasks.filter(t => t.status === "Completed").length,
  };

  // ─── RENDER ────────────────────────────────────────────────────────────────
  return (
    <div className="p-4 select-none">

      {/* ── Top bar ─────────────────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <button
            onClick={() => changeMonth(-1)}
            className="px-3 py-1.5 border border-gray-300 dark:border-[#30363d] rounded-lg text-sm text-gray-700 dark:text-slate-200 hover:bg-gray-100 dark:hover:bg-[#21262d] font-medium transition"
          >
            ‹ Prev
          </button>
          <h2 className="text-base font-bold text-gray-800 dark:text-white w-44 text-center">
            {MONTH_NAMES[month]} {year}
          </h2>
          <button
            onClick={() => changeMonth(1)}
            className="px-3 py-1.5 border border-gray-300 dark:border-[#30363d] rounded-lg text-sm text-gray-700 dark:text-slate-200 hover:bg-gray-100 dark:hover:bg-[#21262d] font-medium transition"
          >
            Next ›
          </button>
          <button
            onClick={() => { setCurrentDate(new Date()); setSelectedDay(null); }}
            className="px-3 py-1.5 border border-blue-300 dark:border-blue-700 text-blue-600 dark:text-blue-400 rounded-lg text-xs font-semibold hover:bg-blue-50 dark:hover:bg-blue-950/40 transition"
          >
            Today
          </button>
        </div>

        {/* Summary strip */}
        <div className="flex gap-3 flex-wrap text-xs">
          <span className="bg-gray-100 dark:bg-[#21262d] text-gray-700 dark:text-slate-300 px-3 py-1.5 rounded-full font-semibold border border-transparent dark:border-[#30363d]">
            {counts.total} tasks this month
          </span>
          {counts.high > 0 && (
            <span className="bg-red-100 dark:bg-red-950/50 text-red-700 dark:text-red-400 px-3 py-1.5 rounded-full font-semibold border border-transparent dark:border-red-900/40">
              🔴 {counts.high} High
            </span>
          )}
          <span className="bg-blue-100 dark:bg-blue-950/50 text-blue-700 dark:text-blue-400 px-3 py-1.5 rounded-full font-semibold border border-transparent dark:border-blue-900/40">
             {counts.inProgress} In Progress
          </span>
          <span className="bg-green-100 dark:bg-green-950/50 text-green-700 dark:text-green-400 px-3 py-1.5 rounded-full font-semibold border border-transparent dark:border-green-900/40">
             {counts.completed} Done
          </span>
        </div>
      </div>

      {/* ── Calendar grid ───────────────────────────────────────────────── */}
      <div className="grid grid-cols-7 border border-gray-200 dark:border-[#30363d] rounded-xl overflow-hidden bg-white dark:bg-[#161b22] shadow-sm">

        {/* Day-of-week headers */}
        {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map(d => (
          <div key={d} className="text-center font-bold py-2.5 bg-gray-50 dark:bg-[#21262d] border-b border-gray-200 dark:border-[#30363d] text-gray-500 dark:text-slate-400 text-xs uppercase tracking-wider">
            {d}
          </div>
        ))}

        {calendarDays.map((day, idx) =>
          day ? (
            <div
              key={idx}
              onClick={() => setSelectedDay(selectedDay?.day === day.day ? null : day)}
              className={`min-h-[110px] border-r border-b border-gray-200 dark:border-[#30363d] p-1.5 text-sm relative transition cursor-pointer
                ${isToday(day.dateStr) ? "bg-blue-50/60 dark:bg-blue-950/30" : "hover:bg-gray-50/70 dark:hover:bg-[#21262d]/50"}
                ${selectedDay?.day === day.day ? "ring-2 ring-inset ring-blue-400" : ""}
              `}
            >
              {/* Date number */}
              <div className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-bold mb-1
                ${isToday(day.dateStr) ? "bg-blue-600 text-white" : "text-gray-400 dark:text-slate-500"}`}>
                {day.day}
              </div>

              {/* Task pills */}
              <div className="space-y-0.5 overflow-y-auto max-h-[72px] pr-0.5 scrollbar-hide">
                {day.tasks.map(t => {
                  const pStyle = PRIORITY_STYLES[t.priority] || PRIORITY_STYLES.Medium;
                  const isDue = t._dueDate === day.dateStr;
                  return (
                    <div
                      key={t.id}
                      onClick={e => { e.stopPropagation(); handleTaskClick(t.id); }}
                      onMouseEnter={e => setHoveredTask({ task: t, x: e.clientX, y: e.clientY })}
                      onMouseLeave={() => setHoveredTask(null)}
                      title={t.title}
                      className={`flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border font-medium truncate cursor-pointer
                        hover:opacity-90 transition ${pStyle.pill}`}
                    >
                      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${pStyle.dot}`} />
                      <span className="truncate">{t.title}</span>
                      {isDue && (
                        <span className="ml-auto text-[9px] bg-white/60 dark:bg-black/40 rounded px-0.5 flex-shrink-0">DUE</span>
                      )}
                    </div>
                  );
                })}
                {day.tasks.length > 3 && (
                  <div className="text-[9px] text-gray-400 dark:text-slate-500 pl-1">+{day.tasks.length - 3} more</div>
                )}
              </div>
            </div>
          ) : (
            <div key={idx} className="min-h-[110px] border-r border-b border-gray-200 dark:border-[#30363d] bg-gray-50/30 dark:bg-[#161b22]/40" />
          )
        )}
      </div>

      {/* ── Hover tooltip ───────────────────────────────────────────────── */}
      {hoveredTask && (
        <TaskTooltip
          task={hoveredTask.task}
          x={hoveredTask.x}
          y={hoveredTask.y}
          getEmployee={getEmployee}
          getProject={getProject}
        />
      )}

      {/* ── Day detail panel (below calendar) ───────────────────────────── */}
      {selectedDay && selectedDay.tasks.length > 0 && (
        <DayDetailPanel
          day={selectedDay}
          getEmployee={getEmployee}
          getProject={getProject}
          onTaskClick={handleTaskClick}
          onClose={() => setSelectedDay(null)}
        />
      )}
    </div>
  );
};

// ─── TOOLTIP ─────────────────────────────────────────────────────────────────

const TaskTooltip = ({ task, x, y, getEmployee, getProject }) => {
  const emp  = getEmployee(task);
  const proj = getProject(task);
  const pStyle = PRIORITY_STYLES[task.priority] || PRIORITY_STYLES.Medium;

  // Keep tooltip inside viewport
  const left = Math.min(x + 12, window.innerWidth - 240);
  const top  = Math.min(y + 12, window.innerHeight - 160);

  return (
    <div
      className="fixed z-50 w-56 bg-white dark:bg-[#161b22] border border-gray-200 dark:border-[#30363d] rounded-xl shadow-xl p-3 pointer-events-none"
      style={{ left, top }}
    >
      <div className="flex items-start gap-2 mb-2">
        <span className={`mt-1 w-2 h-2 rounded-full flex-shrink-0 ${pStyle.dot}`} />
        <p className="text-xs font-bold text-gray-800 dark:text-white leading-tight">{task.title}</p>
      </div>
      <div className="space-y-1 text-[10px] text-gray-500 dark:text-slate-400">
        {proj && (
          <div className="flex gap-1">
            <span className="font-semibold text-gray-400 dark:text-slate-500 w-16 shrink-0">Project:</span>
            <span className="text-blue-600 dark:text-blue-400 font-medium truncate">{proj.name}</span>
          </div>
        )}
        {emp && (
          <div className="flex gap-1">
            <span className="font-semibold text-gray-400 dark:text-slate-500 w-16 shrink-0">Assigned:</span>
            <span className="text-gray-700 dark:text-slate-200 font-medium">{emp.employeeName || emp.name}</span>
          </div>
        )}
        {task._dueDate && (
          <div className="flex gap-1">
            <span className="font-semibold text-gray-400 dark:text-slate-500 w-16 shrink-0">Due:</span>
            <span>{formatDate(task._dueDate)}</span>
          </div>
        )}
        <div className="flex gap-2 mt-1.5 pt-1.5 border-t border-gray-100 dark:border-[#30363d]">
          <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold ${pStyle.pill}`}>
            {task.priority}
          </span>
          <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold ${STATUS_STYLES[task.status] || "bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-slate-300"}`}>
            {task.status}
          </span>
        </div>
      </div>
    </div>
  );
};

// ─── DAY DETAIL PANEL ────────────────────────────────────────────────────────

const DayDetailPanel = ({ day, getEmployee, getProject, onTaskClick, onClose }) => {
  const fmt = (d) => new Date(d + "T00:00:00").toLocaleDateString("en-IN", { weekday:"long", day:"numeric", month:"long", year:"numeric" });

  return (
    <div className="mt-4 bg-white dark:bg-[#161b22] border border-gray-200 dark:border-[#30363d] rounded-xl shadow-sm overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-[#21262d] border-b border-gray-200 dark:border-[#30363d]">
        <div>
          <p className="text-xs font-bold text-gray-800 dark:text-white">{fmt(day.dateStr)}</p>
          <p className="text-[10px] text-gray-400 dark:text-slate-400">{day.tasks.length} task{day.tasks.length !== 1 ? "s" : ""}</p>
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-slate-200 text-lg leading-none px-1">×</button>
      </div>

      <div className="divide-y divide-gray-100 dark:divide-[#30363d]">
        {day.tasks.map(t => {
          const emp   = getEmployee(t);
          const proj  = getProject(t);
          const pStyle = PRIORITY_STYLES[t.priority] || PRIORITY_STYLES.Medium;
          const isDue  = t._dueDate === day.dateStr;

          return (
            <div
              key={t.id}
              onClick={() => onTaskClick(t.id)}
              className="flex items-start gap-3 px-4 py-3 hover:bg-blue-50/30 dark:hover:bg-[#21262d]/60 cursor-pointer transition group"
            >
              {/* Priority dot */}
              <div className={`mt-1.5 w-2.5 h-2.5 rounded-full flex-shrink-0 ${pStyle.dot}`} />

              {/* Main content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-semibold text-gray-800 dark:text-white group-hover:text-blue-700 dark:group-hover:text-blue-400 transition truncate">
                    {t.title}
                  </p>
                  {isDue && (
                    <span className="text-[9px] bg-red-100 dark:bg-red-950/50 text-red-600 dark:text-red-400 px-1.5 py-0.5 rounded font-bold">DUE TODAY</span>
                  )}
                  {!isDue && t._startDate === day.dateStr && (
                    <span className="text-[9px] bg-blue-100 dark:bg-blue-950/50 text-blue-600 dark:text-blue-400 px-1.5 py-0.5 rounded font-bold">STARTS TODAY</span>
                  )}
                </div>

                <div className="flex items-center gap-3 mt-1 flex-wrap">
                  {proj && (
                    <span className="text-[10px] text-blue-600 dark:text-blue-400 font-medium">📁 {proj.name}</span>
                  )}
                  {emp && (
                    <span className="text-[10px] text-gray-500 dark:text-slate-400">
                      👤 {emp.employeeName || emp.name}
                      {emp.role && <span className="text-gray-400 dark:text-slate-500"> · {emp.role}</span>}
                    </span>
                  )}
                  {t.estimatedHours > 0 && (
                    <span className="text-[10px] text-gray-400 dark:text-slate-500">⏱ {t.estimatedHours}h est.</span>
                  )}
                </div>
              </div>

              {/* Right badges */}
              <div className="flex flex-col items-end gap-1 flex-shrink-0">
                <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold border ${pStyle.pill}`}>
                  {t.priority}
                </span>
                <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold ${STATUS_STYLES[t.status] || "bg-gray-100 dark:bg-slate-800 text-gray-500 dark:text-slate-400"}`}>
                  {t.status}
                </span>
                {t.totalCost > 0 && (
                  <span className="text-[9px] text-purple-600 dark:text-purple-400 font-semibold">
                    ₹{Number(t.totalCost).toLocaleString("en-IN")}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TaskCalendarView;